import { useCallback, useEffect, useRef, useState } from "react"
import { BakeryStoreProvider } from "../game/bakeryStore"
import { getDiscordAuth, resetDiscordAuth, warmDiscordAuth } from "./sdk"
import { clearSession, fetchGuilds, fetchMe, getStoredUser, getToken, resetAuthInflight, resolveGuildId, setSession } from "../auth/session"
import type { SessionUser } from "../auth/session"
import { apiFetch } from "../auth/api"
import { usePrefs } from "../contexts/PrefsContext"
import { isSnowflake } from "@cookies/shared/snowflake"
import { BakeryShell, BakerySkeleton } from "../components/BakeryShell"
import { fxCssVars } from "../game/fxTiming"
import { APP_CHANNEL_AUTH_TIMEOUT_MS, API_FETCH_TIMEOUT_MS } from "@cookies/shared/config"

function ChannelBottomNav() {
  // Single source: bottom nav is Bake, Upgrades, Quests, Bakery, Store, Menu — no leaderboard (in Bakery Modules/Leaderboard)
  const items: Array<[string, string]> = [["🍪", "Bake"], ["⚙️", "Upgrades"], ["📜", "Quests"], ["🏭", "Bakery"], ["✨", "Store"], ["☰", "Menu"]]
  return (
    <nav className="bk-nav" aria-label="Bakery sections" aria-hidden="true">
      {items.map((n) => (
        <span key={n[1]} className="bk-navbtn"><span className="bk-nico" aria-hidden="true">{n[0]}</span><span>{n[1]}</span></span>
      ))}
    </nav>
  )
}

function AuthedBakeryWithSignOut({ guildId, token, user, retryKey }: { guildId: string; token: string; user: SessionUser; retryKey: number }) {
  void user;
  return (
    <BakeryStoreProvider key={guildId + ":" + token + ":" + retryKey} guildId={guildId} token={token} userId={user.id} enabled={true}>
      <BakeryShell />
    </BakeryStoreProvider>
  )
}

type MeGuildsResponse = { guilds?: Array<{ id: string }>; user?: { id?: string } }

function readMeGuilds(v: unknown): Array<{ id: string }> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return []
  const r = v as Record<string, unknown>
  const g = r["guilds"]
  if (!Array.isArray(g)) return []
  const out: Array<{ id: string }> = []
  for (const e of g) {
    if (e && typeof e === "object" && !Array.isArray(e)) {
      const rec = e as Record<string, unknown>
      if (typeof rec["id"] === "string") out.push({ id: rec["id"] as string })
    }
  }
  return out
}

function urlGuildId(): string {
  try {
    const q = new URLSearchParams(window.location.search)
    const cand = q.get("guild_id") ?? q.get("guildId")
    if (cand && isSnowflake(cand)) return cand.trim()
  } catch {
    return ""
  }
  return ""
}

function isAbortErr(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError"
}

export default function AppChannelEntry() {
  const prefs = usePrefs()
  const { reduced } = prefs
  const [authed, setAuthed] = useState<{ token: string; user: SessionUser } | null>(null)
  const [guildId, setGuildId] = useState("")
  const [authError, setAuthError] = useState<string | null>(null)
  const [joining, setJoining] = useState(true)
  const [retryNonce, setRetryNonce] = useState(0)
  const mountedRef = useRef(true)
  const runIdRef = useRef(0)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  useEffect(() => {
    let alive = true
    const runId = runIdRef.current + 1
    runIdRef.current = runId
    const isCurrent = (): boolean => alive && mountedRef.current && runIdRef.current === runId
    setJoining(true)
    const run = async (): Promise<void> => {
      let token = ""
      let user: SessionUser | null = null
      try {
        const storedToken = getToken()
        const storedUser = getStoredUser()
        if (storedToken && storedUser) {
          try {
            const fresh = await fetchMe(storedToken)
            if (!isCurrent()) return
            setSession(storedToken, fresh)
            token = storedToken
            user = fresh
            warmDiscordAuth()
          } catch (e: unknown) {
            if (!isCurrent() || isAbortErr(e)) return
            token = ""
            user = null
          }
        }
        if (token === "" || user === null) {
          if (!isCurrent()) return
          try {
            const identity = await Promise.race([
              getDiscordAuth(),
              new Promise<never>((_resolve, reject) => {
                window.setTimeout(() => reject(new Error("auth timed out — Retry")), APP_CHANNEL_AUTH_TIMEOUT_MS)
              }),
            ])
            if (!isCurrent()) return
            setSession(identity.accessToken, identity.user)
            token = identity.accessToken
            user = identity.user
          } catch (e: unknown) {
            if (!isCurrent() || isAbortErr(e)) return
            const msg = e instanceof Error ? e.message : "auth failed"
            setAuthError(msg)
            return
          }
        }
        if (!isCurrent()) return
        let gid = ""
        const direct = resolveGuildId()
        if (direct && isSnowflake(direct.trim())) {
          gid = direct.trim()
        }
        if (gid === "") {
          const viaUrl = urlGuildId()
          if (viaUrl !== "") gid = viaUrl
        }
        if (gid === "" && isCurrent()) {
          try {
            const guilds = await fetchGuilds(token)
            if (!isCurrent()) return
            const found = guilds.find((g) => typeof g.id === "string" && isSnowflake(g.id))
            if (found) gid = found.id.trim()
          } catch (e: unknown) {
            if (!isCurrent() || isAbortErr(e)) return
          }
        }
        if (gid === "" && isCurrent()) {
          try {
            const me = await apiFetch<MeGuildsResponse>("/api/auth/me", { token, timeoutMs: API_FETCH_TIMEOUT_MS })
            if (!isCurrent()) return
            const found = readMeGuilds(me).find((g) => isSnowflake(g.id))
            if (found) gid = found.id.trim()
          } catch (e: unknown) {
            if (!isCurrent() || isAbortErr(e)) return
          }
        }
        if (!isCurrent()) return
        if (gid !== "" && isSnowflake(gid) && user !== null) {
          setGuildId(gid)
          setAuthed({ token, user })
          setAuthError(null)
        } else {
          setAuthError("no guild found - join a server with the app installed")
        }
      } finally {
        if (isCurrent()) setJoining(false)
      }
    }
    void run()
    return () => {
      alive = false
    }
  }, [retryNonce])
  const retry = useCallback(() => {
    clearSession()
    resetDiscordAuth()
    resetAuthInflight()
    runIdRef.current += 1
    setAuthError(null)
    setAuthed(null)
    setGuildId("")
    setJoining(true)
    setRetryNonce((n) => n + 1)
  }, [])
  if (authed && guildId !== "" && isSnowflake(guildId)) {
    return <AuthedBakeryWithSignOut key={guildId + ":" + authed.token + ":" + retryNonce} guildId={guildId} token={authed.token} user={authed.user} retryKey={retryNonce} />
  }
  if (authError) {
    return (
      <div className={"bk-root" + (reduced ? " rm" : "")} style={fxCssVars()}>
        <header className="bk-header"><div className="bk-brand"><span className="bk-brand-ico">🍪</span><span className="bk-brand-text"><span className="bk-brand-name">Bakery</span><span className="bk-brand-lv">Bakery</span></span></div></header>
        <main className="bk-main bk-sub"><div className="bk-wide"><h1>Could not load bakery</h1><p>{authError}</p><button type="button" className="bk-authbtn" onClick={retry}>Retry</button></div></main>
        <ChannelBottomNav />
      </div>
    )
  }
  if (joining) {
    return <BakerySkeleton reduced={reduced} />
  }
  return <BakerySkeleton reduced={reduced} />
}
